import {useEffect, useRef, useState, useCallback} from 'react'
import axios from 'axios'
import {adminStatusUrl} from '../redux/api'

/** Refresh every 30 seconds for more responsive updates */
const REFRESH_INTERVAL_MS = 30_000

/** Response from GET /api/chat/admin-status */
interface AdminStatusResponse {
  online: boolean
}

/**
 * Fetches whether the admin (role 2) is online.
 * Per docs/USER-SOCKET-IO.md: GET /api/chat/admin-status returns { online: true | false }.
 * Any authenticated user can call. Use for non-admin to show "Admin online/offline".
 */
export function useAdminStatus(enabled: boolean) {
  const [online, setOnline] = useState<boolean | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const enabledRef = useRef(enabled)
  const hasSuccessfulFetchRef = useRef(false)

  // Keep enabled ref in sync
  useEffect(() => {
    enabledRef.current = enabled
  }, [enabled])

  const fetchStatus = useCallback(async () => {
    if (!enabledRef.current) return
    
    // Check network status before making request
    if (!navigator.onLine) {
      // Network is offline - silently skip this fetch, will retry when online
      if (process.env.NODE_ENV === 'development') {
        console.debug('[admin-status] Skipping fetch: network offline')
      }
      return
    }
    
    setLoading(true)
    setError(null)
    try {
      const res = await axios.get<AdminStatusResponse>(adminStatusUrl)
      const isOnline = res.data?.online ?? false
      setOnline(isOnline)
      hasSuccessfulFetchRef.current = true
      setError(null) // Clear any previous errors on success
      if (process.env.NODE_ENV === 'development') {
        console.debug('[admin-status] Admin is', isOnline ? 'online' : 'offline')
      }
    } catch (e: any) {
      // Handle network errors - don't clear the last known status
      const isNetworkError =
        e?.code === 'ERR_NETWORK' ||
        e?.code === 'ERR_INTERNET_DISCONNECTED' ||
        e?.message?.includes('ERR_INTERNET_DISCONNECTED') ||
        e?.message?.includes('ERR_NETWORK_CHANGED') ||
        e?.message?.includes('Network Error') ||
        (!e?.response && e?.request) // Request made but no response (network issue)
      
      if (isNetworkError) {
        // Network error - don't show error to user, keep previous data, will retry
        if (process.env.NODE_ENV === 'development') {
          console.debug('[admin-status] Network error, will retry:', e?.message)
        }
        // Don't clear online status - keep showing last known state
        setLoading(false)
        return
      }
      
      // Other errors (e.g. 500) - show error but keep last known status if available
      setError(e?.response?.data?.message || e?.message || 'Failed to load')
      // Only clear status if we never had a successful fetch
      if (!hasSuccessfulFetchRef.current) {
        setOnline(null)
      }
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    if (!enabled) {
      setOnline(null)
      setError(null)
      hasSuccessfulFetchRef.current = false
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
        intervalRef.current = null
      }
      return
    }
    
    // Initial fetch
    fetchStatus()
    
    // Set up interval
    intervalRef.current = setInterval(() => {
      fetchStatus()
    }, REFRESH_INTERVAL_MS)
    
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
        intervalRef.current = null
      }
    }
  }, [enabled, fetchStatus])

  return {online, loading, error}
}
